import React, { useState, useCallback, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { Spin } from "antd";
import { cancelSleepCheck } from "@tailormed/common-client/services/auth";
import { getAuthenticatedRedirectPath } from "@tailormed/common-client/util/deepLinkUtils";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { withOktaAuth } from "@okta/okta-react";
import { compose } from "redux";

import { selectIDPConfig } from "../store/selector";
import OktaSignInWidget from "@tailormed/common-client/component/OktaSignInWidget";
import LoginLayout from "@tailormed/common-client/component/LoginLayout";
import { Routes } from "../constant/routes";
import { oktaConfig } from "../constant/okta";
import { ProductIcon } from "@tailormed/common-client/component/AppShell";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage";
import { getLoginImagesAPI } from "../api/api";

/**
 * Check if the current URL indicates we're returning from an IDP redirect.
 * Okta's IdP Discovery adds ?fromLogin=true when redirecting back after SSO.
 */
const isReturningFromIdp = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("fromLogin") === "true";
};

const LoginComponent = ({ authState, oktaAuth, redirectedToLogin }) => {
  const { t } = useTranslation();
  const idpConfig = useSelector(selectIDPConfig);
  const [images, setImages] = useState({});
  const [isDefaultBackground, setIsDefaultBackground] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(true);

  const getLoginImage = useCallback(async () => {
    try {
      const result = await getLoginImagesAPI();
      if (result.data.success) {
        setImages(result.data.images);
        if (!result.data.images.clientLoginBackground) {
          setIsDefaultBackground(true);
        }
      } else {
        setIsDefaultBackground(true);
      }
    } catch (error) {
      setIsDefaultBackground(true);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    getLoginImage();
  }, [getLoginImage]);

  useEffect(() => {
    // Clear any lingering local storage items
    // related to the handleReturnFromSleep check.
    // Leftover items can lead to unexpected logouts.
    cancelSleepCheck();
    if (window.location.hostname !== "localhost") {
      window.pendo.initialize({
        visitor: {
          id: "VISITOR-UNIQUE-ID",
          role: "anonymous user",
          application: "financialNavigation"
        },
        account: {
          id: `ANONYMOUS-CLIENT-${uuidv4()}`,
          name: "anonymous user account"
        }
      });
    }
  }, []);

  const onSuccess = (res) => {
    if (res.status === "SUCCESS") {
      if (!redirectedToLogin) {
        oktaAuth.setOriginalUri(Routes.ROOT + Routes.SELECT);
      }

      return oktaAuth.signInWithRedirect();
    }
  };

  const onError = (err) => {
    ErrorMessage(`Error logging in ${err}`);
  };

  // If already authenticated, redirect to the original URI (deep link) or ROOT
  if (authState?.isAuthenticated) {
    const redirectTo = getAuthenticatedRedirectPath(oktaAuth, Routes.ROOT);
    return <Redirect to={{ pathname: redirectTo }} />;
  }

  // Show loading state while authentication is pending or returning from IDP
  // This prevents the login form from flashing briefly before redirect
  // Only show loading for IDP return if authState hasn't been determined yet
  // to avoid infinite loading if SSO auth fails
  if (authState?.isPending || (isReturningFromIdp() && !authState)) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#fff"
        }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 16, color: "#666", fontSize: 14 }}>
          {t("authenticating", { defaultValue: "Completing sign in..." })}
        </div>
      </div>
    );
  }

  return (
    <LoginLayout
      productLabel={t("insights", { defaultValue: "Financial Insights" })}
      moduleIcon={<ProductIcon module="insights" size={40} />}
      clientLogo={images.clientLogo}
      siteImage={isDefaultBackground ? undefined : images.clientLoginBackground}
      isSiteImageLoading={imagesLoading}
      contactSupportText={t("login_email_support", { defaultValue: "Email Support" })}
    >
      {!!idpConfig && (
        <OktaSignInWidget
          oktaAuth={oktaAuth}
          oktaBaseUrl={oktaConfig.baseUrl}
          redirectUri={oktaConfig.redirectUri}
          requestContext={oktaConfig.requestContext}
          idpConfig={idpConfig}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}
    </LoginLayout>
  );
};

const Login = compose(withOktaAuth)(LoginComponent);
export { Login };
